import { Link } from "react-router-dom";
import { ArrowRight, Wrench, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { VEHICLES } from "@/config/site";

const Flotte = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: VEHICLES.map((v, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: v.name,
      image: v.image,
    })),
  };

  return (
    <>
      <Seo
        title="Notre flotte de camions et véhicules industriels | ASTROLOG"
        description="Fourgons, porteurs 7,5 T, 12 T et 19 T, semi-remorques 40 T et porte-voitures : découvrez la flotte ASTROLOG pour tous vos transports en France et en Europe."
        path="/flotte"
        jsonLd={jsonLd}
      />

      <section className="brand-dark noise relative py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h1 className="max-w-3xl font-heading text-4xl font-bold tracking-tighter text-[#FAFAF7] sm:text-5xl">
              Notre flotte de véhicules
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Des véhicules récents et entretenus, de l'utilitaire léger à la
              semi-remorque 40 T, tous conduits par des chauffeurs expérimentés.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8" data-testid="fleet-grid">
        {/* Emplacement réservé — remplacer par de vraies photos de la flotte avec l'accord du client. */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {VEHICLES.map((v, i) => (
            <Reveal key={v.id} delay={(i % 3) * 80}>
              <article
                data-testid={`fleet-card-${v.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={v.image}
                    alt={`${v.name} — ASTROLOG`}
                    loading="lazy"
                    width={1200}
                    height={675}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h2 className="font-heading text-xl font-semibold">{v.name}</h2>
                  <p className="mt-1 text-sm font-medium text-primary">{v.ptac}</p>
                  <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <dt className="text-xs uppercase tracking-wider text-slate-500">Charge utile</dt>
                      <dd className="mt-1 font-heading font-semibold">
                        {v.payload ? `${v.payload.toLocaleString("fr-FR")} kg` : "Sur devis"}
                      </dd>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-3">
                      <dt className="text-xs uppercase tracking-wider text-slate-500">Volume</dt>
                      <dd className="mt-1 font-heading font-semibold">
                        {v.volume ? `~${v.volume} m³` : "Sur devis"}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">
                    <span className="font-medium text-slate-700">Usage recommandé : </span>
                    {v.usage}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={150}>
          <div className="mt-14 grid gap-6 rounded-3xl border border-slate-200 bg-white p-8 sm:grid-cols-2">
            <div className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <Wrench className="h-6 w-6 text-primary" strokeWidth={1.75} />
              </span>
              <div>
                <h2 className="font-heading text-lg font-semibold">Entretien rigoureux</h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Véhicules suivis selon les préconisations constructeur et
                  contrôlés avant chaque mission, pour une disponibilité maximale.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <ShieldCheck className="h-6 w-6 text-primary" strokeWidth={1.75} />
              </span>
              <div>
                <h2 className="font-heading text-lg font-semibold">Conformité totale</h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Licence communautaire de transport, capacité professionnelle et
                  assurance marchandises transportées (AXA France IARD).
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-12 text-center">
            <Link
              to="/contact?service=transport-marchandises"
              data-testid="fleet-devis-cta"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#EA580C] hover:shadow-xl"
            >
              Demander un devis pour un véhicule <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Flotte;
